import { useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { connect } from "react-redux";
import { type Dispatch, type UnknownAction } from "redux";

import {
  addExchangeLocationsDetails as addExchangeLocationsDetailsAction,
  exchangeLocationsDetails as exchangeLocationsDetailsAction,
  exchangeLocationsDetailsReset as exchangeLocationsDetailsResetAction,
  updateExchangeLocationsDetails as updateExchangeLocationsDetailsAction,
} from "@/actions";
import { WFPButton } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ALL_ITEMS_PAGE_SIZE } from "@/constants/pagination";

import { type ComboboxOption } from "../../../InfoHub/Components/Form/Components/MultiSelectCombobox";
import { EXCHANGE_LOCATION_FORM_ID, type ExchangeLocationFormValues, Form } from "../Form";
import {
  type EntityId,
  type ExchangeLocation,
  type ExchangeLocationModalOptions,
  type ExchangeLocationType,
  type SetExchangeLocationModalOptions,
  type TraderOrganization,
} from "../../types";

type ExchangeLocationDetailResponse =
  | ExchangeLocation
  | { results?: ExchangeLocation[] }
  | undefined;

interface RootState {
  exchangeLocationsDetail: {
    data: ExchangeLocationDetailResponse;
    isFetching: boolean;
  };
}

interface StateProps {
  detail: ExchangeLocationDetailResponse;
  isFetching: boolean;
}

interface OwnProps {
  areaOptions?: ComboboxOption[];
  exchangeLocationTypes?: ExchangeLocationType[];
  modalOptions?: ExchangeLocationModalOptions;
  setModalOptions: SetExchangeLocationModalOptions;
  traders?: TraderOrganization[];
}

interface DispatchProps {
  addExchangeLocationsDetails: (values: ExchangeLocationPayload) => void;
  exchangeLocationsDetails: (id: EntityId) => void;
  exchangeLocationsDetailsReset: () => void;
  updateExchangeLocationsDetails: (id: EntityId, values: ExchangeLocationPayload) => void;
}

type ExchangeLocationsEditModalProps = StateProps & OwnProps & DispatchProps;
type AppDispatch = Dispatch<UnknownAction>;

export type ExchangeLocationPayload = Omit<ExchangeLocationFormValues, "assignments"> & {
  assignments: { trader_organization: EntityId; types: EntityId[] }[];
};

const CLOSED_MODAL_OPTIONS: ExchangeLocationModalOptions = {
  id: undefined,
  isOpen: false,
  type: undefined,
};

const dispatchAction = (dispatch: AppDispatch, action: unknown) =>
  dispatch(action as UnknownAction);

const isEntityId = (id: ExchangeLocationModalOptions["id"]): id is EntityId =>
  id !== undefined && id !== "add";

const unwrapDetail = (
  detail: ExchangeLocationDetailResponse,
  id: ExchangeLocationModalOptions["id"]
): ExchangeLocation | undefined => {
  if (!detail) {
    return undefined;
  }

  if ("results" in detail && Array.isArray(detail.results)) {
    return detail.results.find(item => String(item.id) === String(id));
  }

  return detail as ExchangeLocation;
};

export const formatInitialValues = (
  detail: ExchangeLocationDetailResponse,
  id: ExchangeLocationModalOptions["id"],
  traders: TraderOrganization[]
): Partial<ExchangeLocationFormValues> | undefined => {
  const exchangeLocation = unwrapDetail(detail, id);

  if (!exchangeLocation) {
    return undefined;
  }

  const assignments = (exchangeLocation.assignments ?? []).flatMap(assignment =>
    assignment.trader_organization === undefined || assignment.trader_organization === null
      ? []
      : [{ id: assignment.trader_organization, list: assignment.types ?? [] }]
  );

  return {
    active: Boolean(exchangeLocation.active),
    assignments,
    location: exchangeLocation.location ?? null,
    name: exchangeLocation.name ?? "",
    traders: assignments.flatMap(assignment => {
      const trader = assignment.list.length
        ? traders.find(candidate => String(candidate.id) === String(assignment.id))
        : undefined;

      return trader ? [{ label: trader.name ?? "", value: trader.id ?? "" }] : [];
    }),
    types: [...new Set(assignments.flatMap(assignment => assignment.list))],
  };
};

const DIALOG_TITLE_KEYS = {
  create: "viewExchangeDetailsModal.title.create",
  edit: "viewExchangeDetailsModal.title.edit",
  view: "viewExchangeDetailsModal.title.view",
} as const;

export const ExchangeLocationsEditModal = ({
  addExchangeLocationsDetails,
  areaOptions = [],
  detail,
  exchangeLocationTypes = [],
  exchangeLocationsDetails,
  exchangeLocationsDetailsReset,
  isFetching,
  modalOptions = CLOSED_MODAL_OPTIONS,
  setModalOptions,
  traders = [],
  updateExchangeLocationsDetails,
}: ExchangeLocationsEditModalProps) => {
  const { t } = useTranslation();
  const { id, isOpen, type } = modalOptions;
  const isViewMode = type === "view";

  useEffect(() => {
    if (!isOpen || !isEntityId(id)) {
      return undefined;
    }

    exchangeLocationsDetails(id);

    return () => {
      exchangeLocationsDetailsReset();
    };
  }, [exchangeLocationsDetails, exchangeLocationsDetailsReset, id, isOpen]);

  const initialValues = useMemo(
    () => (type === "create" ? undefined : formatInitialValues(detail, id, traders)),
    [detail, id, traders, type]
  );

  const handleClose = () => {
    setModalOptions(CLOSED_MODAL_OPTIONS);
  };

  const handleSubmit = (formValues: ExchangeLocationFormValues) => {
    // ponytail: `traders` and `types` are UI-only but stay in the body, as the legacy form sent
    // them; drop once the serializer is confirmed to ignore unknown fields.
    const payload: ExchangeLocationPayload = {
      ...formValues,
      assignments: formValues.assignments.map(assignment => ({
        trader_organization: assignment.id,
        types: assignment.list.filter(assignmentType =>
          formValues.types.some(selectedType => String(selectedType) === String(assignmentType))
        ),
      })),
    };

    if (type === "edit" && isEntityId(id)) {
      updateExchangeLocationsDetails(id, payload);
    }

    if (type === "create") {
      addExchangeLocationsDetails(payload);
    }

    handleClose();
  };

  return (
    <Dialog open={Boolean(isOpen)} onOpenChange={open => (open ? undefined : handleClose())}>
      <DialogContent aria-describedby={undefined} className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t(DIALOG_TITLE_KEYS[type ?? "view"])}</DialogTitle>
        </DialogHeader>
        <div className="max-h-[65vh] overflow-y-auto px-1">
          <Form
            areaOptions={areaOptions}
            exchangeLocationTypes={exchangeLocationTypes}
            initialValues={initialValues}
            isFetching={isFetching}
            modalOptions={modalOptions}
            traders={traders}
            onSubmit={handleSubmit}
          />
        </div>
        <DialogFooter>
          {isViewMode ? null : (
            <WFPButton type="button" variant="outline" onClick={handleClose}>
              {t("common.cancel")}
            </WFPButton>
          )}
          {isViewMode ? (
            <WFPButton type="button" onClick={handleClose}>
              {t("common.back")}
            </WFPButton>
          ) : (
            <WFPButton type="submit" form={EXCHANGE_LOCATION_FORM_ID}>
              {t("common.save")}
            </WFPButton>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const mapStateToProps = (state: RootState): StateProps => {
  return {
    detail: state.exchangeLocationsDetail.data,
    isFetching: state.exchangeLocationsDetail.isFetching,
  };
};

export const mapDispatchToProps = (dispatch: AppDispatch): DispatchProps => {
  return {
    addExchangeLocationsDetails: values =>
      dispatchAction(
        dispatch,
        addExchangeLocationsDetailsAction(values, "", true, { page_size: ALL_ITEMS_PAGE_SIZE })
      ),
    exchangeLocationsDetails: id =>
      dispatchAction(dispatch, exchangeLocationsDetailsAction(String(id))),
    exchangeLocationsDetailsReset: () =>
      dispatchAction(dispatch, exchangeLocationsDetailsResetAction()),
    updateExchangeLocationsDetails: (id, values) =>
      dispatchAction(
        dispatch,
        updateExchangeLocationsDetailsAction(id, values, "", true, {
          page_size: ALL_ITEMS_PAGE_SIZE,
        })
      ),
  };
};

export const ConnectedExchangeLocationsEditModal = connect(
  mapStateToProps,
  mapDispatchToProps
)(ExchangeLocationsEditModal);
